/**
 * ReviewThreadModal — presents the full interaction thread for an App Store
 * review (the original review, the developer reply, and any subsequent
 * customer follow-up).
 *
 * Mounted at the AppShell level (sibling of CustomerHistoryModal), driven by
 * `useQueueStore.reviewThreadModalInteractionId`. Mirrors CustomerHistoryModal
 * exactly, so the operator gets an identical workflow across support email
 * history and review threads, keyed by interaction id instead of email.
 *
 * ## Two hosts, one page (Task 4094)
 *
 * **Desktop (`IS_TAURI`)** — unchanged. Renders nothing in the main DOM; the
 * "modal" IS a new OS window. When an interaction id becomes non-null it:
 *   1. Derives a stable Tauri window label from the id (prefix `rt-`).
 *   2. Closes any existing window with that label (prevents stale data) and
 *      creates a new `WebviewWindow` loading `index.html?rt=1` with the
 *      interaction id, reviewer name, app name, consumerId and env as params —
 *      both handled inside `openPopout`.
 *   3. Immediately calls `closeReviewThreadModal()` so the store is reset,
 *      allowing the same review to be re-opened later.
 *
 * **Browser** — renders `ReviewThreadPage` as an in-page `PopoutPanel` overlay
 * inside the live app. No OS window and deliberately no `window.open`; see the
 * rationale in `PopoutPanel.tsx`. The store state persists while the panel is
 * open and clears on dismiss, rather than resetting immediately the way the
 * desktop path does once the OS window owns the data.
 *
 * Re-opening the same review presents fresh data in both hosts: on desktop by
 * recreating the window, in the browser by keying the panel on
 * `reviewThreadModalOpenCount`, which the store bumps on every open.
 */

import { useEffect, type ReactNode } from 'react'
import { openPopout } from '@/lib/popout'
import { IS_TAURI } from '@/lib/platform'
import { useQueueStore } from '../../store'
import { useOperatorStore } from '@/shared/stores/operator-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import { toast } from '@/shared/hooks/use-toast'
import { PopoutPanel } from '../PopoutPanel'
import { ReviewThreadPage } from '@/pages/ReviewThreadPage'

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Derive a stable, label-safe Tauri window identifier for a review thread.
 * Tauri window labels must match `[a-zA-Z0-9_-/]+`, max 64 chars.
 * We prefix with `rt-` so the capability's `"rt-*"` glob matches.
 */
function reviewThreadLabel(interactionId: string): string {
  return `rt-${interactionId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`
}

// ── Desktop: a real OS pop-out window ────────────────────────────────────────

function ReviewThreadPopoutWindow(): ReactNode {
  const interactionId = useQueueStore((s) => s.reviewThreadModalInteractionId)
  const reviewerName  = useQueueStore((s) => s.reviewThreadModalReviewerName)
  const appName       = useQueueStore((s) => s.reviewThreadModalAppName)

  useEffect(() => {
    if (!interactionId) return

    const openThreadWindow = async (): Promise<void> => {
      const consumerId = useOperatorStore.getState().selected?.consumer_id ?? ''
      const env        = useEnvironmentStore.getState().active

      const label = reviewThreadLabel(interactionId)

      const params = new URLSearchParams()
      params.set('interactionId', interactionId)
      if (reviewerName) params.set('reviewerName', reviewerName)
      if (appName)      params.set('appName', appName)
      if (consumerId)   params.set('consumerId', consumerId)
      params.set('env', env)

      const windowTitle = reviewerName ?? appName ?? 'Review Thread'

      // Closing any existing window with this label, so the new one always
      // fetches the latest data, is handled inside openPopout (Task 4093).
      const result = await openPopout({
        label,
        url:       `index.html?rt=1&${params.toString()}`,
        title:     windowTitle,
        width:     900,
        height:    1040,
        minWidth:  600,
        minHeight: 600,
      })
      if (!result.ok) {
        // `unsupported` is unreachable here — this component only mounts under
        // IS_TAURI, where openPopout takes the WebviewWindow path.
        console.error('[ReviewThreadModal] Failed to create window:', result.error)
        toast({
          title: 'Could not open review thread',
          description: 'The window failed to open. Try again.',
          variant: 'danger',
        })
      }

      // Reset store immediately so the same review can be re-opened.
      useQueueStore.getState().closeReviewThreadModal()
    }

    void openThreadWindow()
  }, [interactionId, reviewerName, appName])

  return null
}

// ── Browser: an in-page panel over the live app ──────────────────────────────

function ReviewThreadInPagePanel(): ReactNode {
  const interactionId = useQueueStore((s) => s.reviewThreadModalInteractionId)
  const reviewerName  = useQueueStore((s) => s.reviewThreadModalReviewerName)
  const appName       = useQueueStore((s) => s.reviewThreadModalAppName)
  const openCount     = useQueueStore((s) => s.reviewThreadModalOpenCount)
  const close         = useQueueStore((s) => s.closeReviewThreadModal)

  if (!interactionId) return null

  const title = reviewerName ?? appName ?? 'Review Thread'

  return (
    <PopoutPanel
      title={`Review thread — ${title}`}
      description="Original review, developer reply and any follow-up. Press Escape to close."
      open
      onClose={close}
    >
      {/* See CustomerHistoryModal — the key is what makes re-open mean fresh. */}
      <ReviewThreadPage
        key={`${interactionId}|${openCount}`}
        interactionId={interactionId}
        reviewerName={reviewerName}
        appName={appName}
        embedded
      />
    </PopoutPanel>
  )
}

// ── Component ────────────────────────────────────────────────────────────────

export function ReviewThreadModal(): ReactNode {
  // A build-time constant, so each bundle keeps only its own branch.
  return IS_TAURI ? <ReviewThreadPopoutWindow /> : <ReviewThreadInPagePanel />
}

export default ReviewThreadModal
